import { type Brand } from '@/models/account-management/Brand';
import { type Location } from '@/models/account-management/Location';

/**
 * Restricting what a user sees to the locations they are actually assigned to (TTD-160).
 *
 * Lives beside `usePermissions` rather than under `components/layout` because it stopped being the top-bar
 * picker's private business: QA found the picker filtered while Onboarding, Tips, Transactions and the Media
 * Network schedules still offered the brand's whole estate, each from its own copy of the same query.
 *
 * Two rules that are easy to get backwards, which is why they live here rather than inline in each screen:
 *
 * 1. **Empty means none.** A user with no assignments sees no locations. Reading "unassigned" as
 *    "unrestricted" is exactly the bug being fixed — it is how someone ended up looking at the whole org.
 *
 * 2. **Unknown is not empty.** Until the current user has loaded — or if the API predates the field
 *    entirely — we do not know the assignment. Filtering against an assumed-empty set would blank the
 *    pickers, and worse, the picker's own stale-selection reconciliation would read that empty list as
 *    "your saved brand/location was deleted" and clear it. Callers pass `undefined` for unknown and get
 *    `undefined` back, which every caller already treats as "not loaded yet".
 *
 * Super-admins are not scoped at all; the picker decides that and simply does not call these.
 *
 * None of this is enforcement. The API still serves any location in the caller's org — see TTD-164.
 */

/** The user's assigned location ids, or `undefined` when we do not yet know them. */
export type Assignment = ReadonlySet<string> | undefined;

/**
 * Anything keyed by a location, restricted to the assigned ones.
 *
 * Generic because the lists that need this are not all lists of locations — the onboarding queue is a list of
 * per-location progress rows, and it leaked the whole brand's estate the same way.
 */
export const assignedOnly = <T>(
    items: T[] | undefined,
    locationIdOf: (item: T) => string | undefined,
    assigned: Assignment
): T[] | undefined => {
    if (!items || !assigned) return undefined;
    return items.filter((item) => {
        const id = locationIdOf(item);
        return !!id && assigned.has(id);
    });
};

export const assignedLocations = (
    locations: Location[] | undefined,
    assigned: Assignment
): Location[] | undefined => assignedOnly(locations, (location) => location.id, assigned);

/**
 * Brands the user can reach at least one location of.
 *
 * A brand with nothing reachable in it is a dead end — selecting it just empties the location picker — so
 * it is not offered. Brands must arrive carrying their locations; one that carries none is treated as
 * unreachable rather than as "no restriction", for the same reason as rule 1 above.
 */
export const assignedBrands = (brands: Brand[] | undefined, assigned: Assignment): Brand[] | undefined => {
    if (!brands || !assigned) return undefined;
    return brands.filter((brand) => brand.locations?.some((location) => !!location.id && assigned.has(location.id)));
};
